import React, { useState } from "react";
import emailjs from "@emailjs/browser";
import {
  getEmailJsPublicKey,
  getEmailJsServiceId,
  initEmailJs,
  emailJsSendFormOptions,
  emailJsEnvLooksMisconfigured,
} from "../lib/emailjsConfig";

initEmailJs();

export const Pricing = ({ data }) => {
  const [showPricing, setShowPricing] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [contactForm, setContactForm] = useState({
    name: "",
    email: "",
    business: "",
    phone: "",
    message: "",
  });
  const [submitStatus, setSubmitStatus] = useState({ type: "", message: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setContactForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const pk = getEmailJsPublicKey();
    const sid = getEmailJsServiceId();

    if (!pk || !sid) {
      setSubmitStatus({
        type: "error",
        message: emailJsEnvLooksMisconfigured()
          ? "EmailJS is still set to Azure pipeline placeholders (#{…}#). Put your real Service ID and Public Key in .env or .local_env, then restart npm start."
          : "Email service is not configured. Please contact us directly at info@bytheapp.com",
      });
      return;
    }

    const form = e.target;
    let toEmailInput = form.querySelector('input[name="to_email"]');
    if (!toEmailInput) {
      toEmailInput = document.createElement("input");
      toEmailInput.type = "hidden";
      toEmailInput.name = "to_email";
      form.appendChild(toEmailInput);
    }
    toEmailInput.value = "info@bytheapp.com";

    setSubmitStatus({ type: "", message: "" });
    setIsSubmitting(true);

    emailjs
      .sendForm(sid, "template_he45gmv", form, emailJsSendFormOptions())
      .then(
        () => {
          setSubmitStatus({
            type: "success",
            message:
              "Thank you for your interest! We'll contact you within 24 hours with detailed pricing information.",
          });
          setFormSubmitted(true);
          setContactForm({
            name: "",
            email: "",
            business: "",
            phone: "",
            message: "",
          });
          setTimeout(() => setSubmitStatus({ type: "", message: "" }), 5000);
        },
        (error) => {
          console.error("EmailJS Error:", error);
          setSubmitStatus({
            type: "error",
            message:
              "Failed to send message. Please try again or email us directly at info@bytheapp.com",
          });
        }
      )
      .finally(() => setIsSubmitting(false));
  };

  const plans = data?.plans ?? [];
  const pilot = data?.pilot;

  return (
    <div id="pricing" className="pricing-section">
      <div className="container">
        <div className="pricing-header">
          <span className="pricing-eyebrow">{data?.eyebrow ?? "Pricing"}</span>
          <h2 className="pricing-title">
            {data?.title ??
              "AI Voice Agents & Business Automation for Every Business"}
          </h2>
          {data?.lead && <p className="pricing-lead">{data.lead}</p>}
          {data?.supporting && (
            <p className="pricing-supporting">{data.supporting}</p>
          )}
        </div>

        {!showPricing ? (
          <div className="row">
            <div className="col-md-8 col-md-offset-2">
              <div className="pricing-intro">
                <h3 className="pricing-form-title">
                  {data?.formTitle ?? "Get Started Today"}
                </h3>
                <p>
                  {data?.formDescription ??
                    "Tell us about your business and we'll recommend a plan tailored to your goals."}
                </p>

                <form onSubmit={handleSubmit} className="pricing-form">
                  <div className="row">
                    <div className="col-md-6">
                      <div className="form-group">
                        <input
                          type="text"
                          name="name"
                          className="form-control"
                          placeholder="Your Name"
                          required
                          value={contactForm.name}
                          onChange={handleInputChange}
                        />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-group">
                        <input
                          type="email"
                          name="email"
                          className="form-control"
                          placeholder="Email Address"
                          required
                          value={contactForm.email}
                          onChange={handleInputChange}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-md-6">
                      <div className="form-group">
                        <input
                          type="text"
                          name="business"
                          className="form-control"
                          placeholder="Business Name"
                          required
                          value={contactForm.business}
                          onChange={handleInputChange}
                        />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-group">
                        <input
                          type="tel"
                          name="phone"
                          className="form-control"
                          placeholder="Phone Number"
                          value={contactForm.phone}
                          onChange={handleInputChange}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="form-group">
                    <textarea
                      name="message"
                      className="form-control"
                      rows="4"
                      placeholder="Tell us about your business, use case, and expected call volume..."
                      value={contactForm.message}
                      onChange={handleInputChange}
                    />
                  </div>

                  <div className="form-status">
                    {submitStatus.message && (
                      <div
                        className={`alert ${submitStatus.type === "success" ? "alert-success" : "alert-danger"}`}
                        role="alert"
                      >
                        {submitStatus.message}
                      </div>
                    )}
                  </div>

                  <button
                    type="submit"
                    className="btn btn-custom btn-lg"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Sending…" : "Get Custom Pricing"}
                  </button>

                  {formSubmitted && (
                    <div className="pricing-access">
                      <p className="pricing-access-text">
                        Ready to see our detailed pricing? Click below to view
                        all available plans.
                      </p>
                      <button
                        type="button"
                        className="btn btn-custom btn-lg"
                        onClick={() => setShowPricing(true)}
                      >
                        View Detailed Pricing
                      </button>
                    </div>
                  )}
                </form>
              </div>
            </div>
          </div>
        ) : (
          <div className="pricing-plans">
            <div className="row">
              <div className="col-md-12">
                <div className="pricing-toggle">
                  <button
                    className="btn btn-default"
                    onClick={() => setShowPricing(false)}
                  >
                    ← Back to Contact Form
                  </button>
                </div>
              </div>
            </div>

            <div className="row pricing-cards">
              {plans.map((plan, i) => (
                <div key={`${plan.name}-${i}`} className="col-md-4">
                  <div
                    className={`pricing-card${plan.featured ? " pricing-card-featured" : ""}`}
                  >
                    <p className="pricing-card-audience">{plan.audience}</p>
                    <h3 className="pricing-card-name">{plan.name}</h3>
                    <div className="pricing-card-price">
                      <span className="pricing-card-amount">{plan.price}</span>
                      <span className="pricing-card-period">{plan.period}</span>
                    </div>
                    <ul className="pricing-card-features">
                      {plan.features.map((feature, j) => (
                        <li key={`${plan.name}-feature-${j}`}>{feature}</li>
                      ))}
                    </ul>
                    {plan.overage && (
                      <p className="pricing-card-overage">{plan.overage}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {pilot && (
              <div className="row">
                <div className="col-md-10 col-md-offset-1">
                  <div className="pricing-pilot">
                    <span className="pricing-pilot-badge">{pilot.badge}</span>
                    <h3>{pilot.name}</h3>
                    <div className="pricing-pilot-price">
                      <span className="pricing-pilot-amount">{pilot.price}</span>
                      {pilot.altPrice && (
                        <span className="pricing-pilot-alt">{pilot.altPrice}</span>
                      )}
                    </div>
                    <p className="pricing-pilot-label">What&apos;s included:</p>
                    <div className="row">
                      <div className="col-md-6">
                        <ul className="pricing-pilot-features">
                          {pilot.featuresLeft.map((item, i) => (
                            <li key={`pilot-left-${i}`}>{item}</li>
                          ))}
                        </ul>
                      </div>
                      <div className="col-md-6">
                        <ul className="pricing-pilot-features">
                          {pilot.featuresRight.map((item, i) => (
                            <li key={`pilot-right-${i}`}>{item}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
